/**
 * The seam between the OBR storage adapter and the vendored rules core.
 *
 * The UI never touches storage or the rules engine directly — it goes through
 * this service. That keeps the React layer ignorant of both OBR metadata and
 * the effective-character machinery, exactly as the parent app's UI is.
 */
import type { StoragePort } from '../obr/storagePort.ts';
import type { Character } from '@dcc/rules/domain/character.ts';
import { buildEffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { deriveStatsFor, type DerivedStats } from '@dcc/rules/app/derivedStats.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import type { EffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import type { Effect } from '@dcc/rules/rules/effects.ts';

/** A stored character plus its computed effective view — everything the sheet
 * needs to render, built through the same code path the desktop app uses. */
export interface SheetView {
  character: Character;
  effective: EffectiveCharacter;
  derived: DerivedStats;
}

/** Item lookup bound to the core catalogue, so equipped gear contributes to
 * effective stats exactly as it does in the desktop build. */
const lookupItem = (id: string) => contentRegistry.getItem(id);

export function computeSheet(character: Character, effects: readonly Effect[] = []): SheetView {
  const effective = buildEffectiveCharacter(character, effects, lookupItem);
  const derived = deriveStatsFor(effective);
  return { character, effective, derived };
}

export class CharacterService {
  constructor(private storage: StoragePort) {}

  async listIds(): Promise<string[]> {
    return this.storage.listCharacterIds();
  }

  /** Load a stored character and compute its sheet view, or null if absent. */
  async loadSheet(id: string): Promise<SheetView | null> {
    const raw = await this.storage.readCharacter(id);
    if (raw == null) return null;
    // The stored blob is a Character (written by this same app). The parent
    // project owns migrations; this slice reads current-schema characters.
    return computeSheet(raw as Character);
  }

  async save(character: Character): Promise<void> {
    await this.storage.writeCharacter(character.id, character);
  }
}
